import { useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { CheckCircle2, FileUp, Info, ListChecks } from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { notificar } from '@/shared/ui/avisos';
import {
  CampoDelPanel,
  PanelLateral,
  claseEntradaPanel,
} from '@/shared/ui/PanelLateral';
import { enDinero } from '@/shared/ui/graficos';
import { mensajeDe } from './mensajes';
import type { ListadoImportado, Opcion } from './contrato';

const TAMANIO_MAXIMO = 10 * 1024 * 1024;

export function PanelDelListado({
  sustentos,
  alCerrar,
  alImportar,
}: {
  sustentos: Opcion[];
  alCerrar: () => void;
  alImportar: () => void;
}) {
  const [archivo, setArchivo] = useState<File | null>(null);
  const [sustento, setSustento] = useState('02');
  const [error, setError] = useState<string | null>(null);
  const [hecho, setHecho] = useState<ListadoImportado | null>(null);
  const selector = useRef<HTMLInputElement>(null);

  const importar = useMutation({
    mutationFn: () => {
      const formulario = new FormData();
      formulario.append('archivo', archivo as File);
      formulario.append('sustento', sustento);
      return api.subir<ListadoImportado>('/compras/listado', formulario);
    },
    onSuccess: (r) => {
      notificar.exito(
        `${r.importados} comprobantes por ${enDinero(r.total)}`,
        'Listado cargado',
      );
      setHecho(r);
      alImportar();
    },
    onError: (causa) => {
      const mensaje = mensajeDe(causa, 'No se pudo cargar el listado');
      setError(mensaje);
      notificar.error(mensaje);
    },
  });

  function elegir(f: File | undefined) {
    setError(null);
    if (!f) return;

    if (!/\.(txt|csv|tsv)$/i.test(f.name)) {
      setError('EL ARCHIVO NO ES EL LISTADO: SE ESPERA UN TXT');
      return;
    }
    if (f.size > TAMANIO_MAXIMO) {
      setError('EL LISTADO PASA DE 10 MB');
      return;
    }
    setArchivo(f);
  }

  return (
    <PanelLateral
      abierto
      alCerrar={alCerrar}
      icono={ListChecks}
      titulo="Cargar el listado del SRI"
      descripcion="Comprobantes recibidos · para registrar el mes y conciliar lo que falta"
      {...(hecho
        ? {}
        : {
            alEnviar: (evento: React.FormEvent) => {
              evento.preventDefault();
              if (archivo) {
                importar.mutate();
              }
            },
            enviando: importar.isPending,
          })}
    >
      {hecho ? (
        <div className="space-y-4">
          <div className="rounded-2xl border border-marca-200 bg-marca-50 p-6 text-center">
            <CheckCircle2 className="mx-auto size-10 text-marca-600" aria-hidden />
            <p className="mt-3 text-sm font-bold uppercase text-marca-900">
              {hecho.importados} {hecho.importados === 1 ? 'comprobante' : 'comprobantes'} ·{' '}
              {enDinero(hecho.total)}
            </p>
            <p className="mt-1 text-[11px] uppercase text-marca-800">
              Esperan su XML para poder declararse
            </p>
          </div>

          <dl className="grid grid-cols-2 gap-2 text-[11px]">
            <Cuenta rotulo="Ya estaban" valor={hecho.yaEstaban} />
            <Cuenta rotulo="Proveedores nuevos" valor={hecho.proveedoresCreados} />
            <Cuenta rotulo="De otra empresa" valor={hecho.ajenos} alerta />
            <Cuenta rotulo="Líneas ilegibles" valor={hecho.ilegibles} alerta />
          </dl>

          {hecho.errores.length > 0 && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
              <p className="text-[10px] font-bold uppercase tracking-wide text-amber-900">
                Lo que no se pudo leer
              </p>
              <ul className="mt-2 space-y-1">
                {hecho.errores.map((e) => (
                  <li key={e} className="text-[11px] uppercase text-amber-900">
                    {e}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <button
            type="button"
            onClick={alCerrar}
            className="inline-flex h-10 w-full items-center justify-center rounded-lg border border-armazon-200 bg-white px-4 text-xs font-semibold uppercase text-armazon-700 hover:bg-armazon-50"
          >
            Cerrar
          </button>
        </div>
      ) : (
        <div className="space-y-5">
          <div>
            <span className="text-sm font-semibold uppercase text-armazon-900">
              Listado descargado del SRI
            </span>
            <input
              ref={selector}
              type="file"
              accept=".txt,.csv,.tsv,text/plain"
              className="hidden"
              onChange={(e) => elegir(e.target.files?.[0])}
            />
            <button
              type="button"
              onClick={() => selector.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                elegir(e.dataTransfer.files?.[0]);
              }}
              className={`mt-2 flex w-full items-center gap-3 rounded-xl border border-dashed px-4 py-6 text-left text-xs transition-colors ${
                archivo
                  ? 'border-marca-300 bg-marca-50 text-marca-800'
                  : 'border-armazon-300 text-armazon-500 hover:bg-armazon-50'
              }`}
            >
              <FileUp className="size-5 shrink-0" aria-hidden />
              <span className="min-w-0">
                {archivo ? (
                  <>
                    <span className="block truncate font-semibold">{archivo.name}</span>
                    <span className="block text-[11px]">{(archivo.size / 1024).toFixed(0)} KB</span>
                  </>
                ) : (
                  <>
                    <span className="block font-semibold uppercase">
                      Elija el TXT o suéltelo aquí
                    </span>
                    <span className="block text-[11px] uppercase">
                      El de «Comprobantes recibidos», tal como se descarga
                    </span>
                  </>
                )}
              </span>
            </button>
          </div>

          <CampoDelPanel
            etiqueta="Sustento tributario"
            requerido
            ayuda="El que se aplica a todo el listado; se corrige uno a uno al cargar sus XML"
          >
            <select
              value={sustento}
              onChange={(e) => setSustento(e.target.value)}
              className={claseEntradaPanel}
            >
              {sustentos.map((s) => (
                <option key={s.valor} value={s.valor}>
                  {s.valor} · {s.etiqueta.toLocaleUpperCase('es')}
                </option>
              ))}
            </select>
          </CampoDelPanel>

          <p className="flex gap-3 rounded-xl border border-armazon-200 bg-armazon-50/60 p-4 text-justify text-[11px] uppercase leading-relaxed text-armazon-600">
            <Info className="mt-0.5 size-4 shrink-0" aria-hidden />
            <span>
              El listado trae la cabecera y poco más: no separa las bases por tarifa ni trae el
              detalle. Lo que entre queda <span className="font-semibold">pendiente de su XML</span>,
              que es el documento que vale para declarar. Subir el mismo listado dos veces no
              duplica nada.
            </span>
          </p>

          {(error ?? (archivo ? null : 'Elija el listado descargado del SRI')) && (
            <p
              role={error ? 'alert' : undefined}
              className={`text-[11px] font-semibold uppercase ${
                error ? 'text-red-700' : 'text-armazon-500'
              }`}
            >
              {error ?? 'Elija el listado descargado del SRI'}
            </p>
          )}
        </div>
      )}
    </PanelLateral>
  );
}

function Cuenta({
  rotulo,
  valor,
  alerta = false,
}: {
  rotulo: string;
  valor: number;
  alerta?: boolean;
}) {
  return (
    <div
      className={`rounded-lg border p-3 ${
        alerta && valor > 0 ? 'border-amber-200 bg-amber-50' : 'border-armazon-200 bg-white'
      }`}
    >
      <dt className="text-[10px] font-semibold uppercase text-armazon-500">{rotulo}</dt>
      <dd
        className={`importe mt-0.5 text-lg font-bold ${
          alerta && valor > 0 ? 'text-amber-800' : 'text-armazon-800'
        }`}
      >
        {valor}
      </dd>
    </div>
  );
}
